import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, Star, Upload, FileText, X, Eye, Download, Building2, Phone, Mail, MapPin, CreditCard, ChevronDown, ChevronUp, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { vendorAPI, fileAPI, fetchPaged } from '../../services/api';
import api from '../../services/api';
import ContactImportWizard from '../ui/ContactImportWizard';
import { exportToCSV } from '../../utils/exportCSV';
import { toast } from 'sonner';

const fmtINR = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(2)} L`;
  return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const CATEGORIES = ['Raw Material', 'Packaging', 'Services', 'Equipment', 'Transport', 'Consumables', 'Trading', 'Other'];
const PAYMENT_TERMS = ['Advance', '15 Days', '30 Days', '45 Days', '60 Days', '90 Days'];
const DOC_TYPES = [
  { value: 'cancelled_cheque', label: 'Cancelled Cheque' },
  { value: 'gst_certificate', label: 'GST Certificate' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'msme', label: 'MSME Certificate' },
  { value: 'agreement', label: 'Agreement / Contract' },
  { value: 'other', label: 'Other KYC' },
];

const Stars = ({ value, onChange, readonly }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(s => (
      <Star key={s} className={`w-4 h-4 cursor-pointer transition-colors ${s <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
        onClick={() => !readonly && onChange?.(s)} />
    ))}
  </div>
);

const emptyForm = {
  name: '', location: '', address: '', city: '', pincode: '', currency: 'INR', type: 'domestic', materials: '',
  contact_person: '', email: '', phone: '', gstin: '', gstin_type: '', pan: '', state: '',
  party_branch: '', category: '', payment_terms: '', credit_limit: 0, rating: 0, notes: '',
  bank_details: { account_number: '', ifsc_code: '', bank_name: '', branch_name: '' },
};

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, create, edit, detail
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterTerms, setFilterTerms] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docType, setDocType] = useState('other');
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const canEdit = ['Admin', 'Manager'].includes(user.role);

  const loadVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 500 };
      if (filterCat) params.category = filterCat;
      if (filterTerms) params.payment_terms = filterTerms;
      if (sortBy) params.sort_by = sortBy;
      const data = await vendorAPI.getAll(params);
      setVendors(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterCat, filterTerms, sortBy]);

  useEffect(() => { loadVendors(); }, [loadVendors]);

  const filtered = useMemo(() => {
    if (!search) return vendors;
    const q = search.toLowerCase();
    return vendors.filter(v =>
      v.name?.toLowerCase().includes(q) ||
      v.contact_person?.toLowerCase().includes(q) ||
      v.gstin?.toLowerCase().includes(q) ||
      v.location?.toLowerCase().includes(q)
    );
  }, [vendors, search]);

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Vendor name is required');
    setSubmitting(true);
    try {
      const payload = { ...form, credit_limit: parseFloat(form.credit_limit) || 0 };
      if (view === 'edit' && selected) {
        await vendorAPI.update(selected.id, payload);
      } else {
        await vendorAPI.create(payload);
      }
      setView('list');
      setForm({ ...emptyForm });
      setSelected(null);
      loadVendors();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
    finally { setSubmitting(false); }
  };

  const handleEdit = (v) => {
    setForm({
      name: v.name || '', location: v.location || '', address: v.address || '',
      city: v.city || '', pincode: v.pincode || '', currency: v.currency || 'INR',
      type: v.type || 'domestic', materials: v.materials || '',
      contact_person: v.contact_person || '', email: v.email || '', phone: v.phone || '',
      gstin: v.gstin || '', gstin_type: v.gstin_type || '', pan: v.pan || '', state: v.state || '',
      party_branch: v.party_branch || '', category: v.category || '', payment_terms: v.payment_terms || '',
      credit_limit: v.credit_limit || 0, rating: v.rating || 0, notes: v.notes || '',
      bank_details: v.bank_details || { account_number: '', ifsc_code: '', bank_name: '', branch_name: '' },
    });
    setSelected(v);
    setView('edit');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try { await vendorAPI.delete(id); loadVendors(); } catch (e) { alert('Delete failed'); }
  };

  const handleImportTally = async () => {
    if (!window.confirm('Import vendors from Tally creditors? Existing vendors will be skipped.')) return;
    setImporting(true);
    try {
      const r = await api.post('/vendors/import-from-tally');
      alert(`Imported ${r.data.imported} vendors (${r.data.skipped} skipped as duplicates)`);
      loadVendors();
    } catch (e) { alert(e.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const handleDocUpload = async (vendorId, file) => {
    setUploadingDoc(true);
    try {
      await api.post(`/vendors/${vendorId}/documents?doc_type=${docType}`, (() => { const fd = new FormData(); fd.append('file', file); return fd; })(), { headers: { 'Content-Type': 'multipart/form-data' } });
      // Refresh vendor detail
      const updated = await vendorAPI.getById(vendorId);
      setSelected(updated);
      setVendors(prev => prev.map(v => v.id === vendorId ? { ...v, documents: updated.documents } : v));
    } catch (e) { alert(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingDoc(false); }
  };

  const handleDocDelete = async (vendorId, docId) => {
    try {
      await api.delete(`/vendors/${vendorId}/documents/${docId}`);
      const updated = await vendorAPI.getById(vendorId);
      setSelected(updated);
    } catch (e) { console.error(e); }
  };

  const openDetail = async (v) => {
    try {
      const full = await vendorAPI.getById(v.id);
      setSelected(full);
      setView('detail');
    } catch (e) { console.error(e); }
  };

  const handleExportVendors = async () => {
    try {
      setExporting(true);
      let allVendors = [];
      let pg = 1;
      while (true) {
        const result = await fetchPaged('/vendors', { page: pg, page_size: 200 });
        allVendors = allVendors.concat(result.items);
        if (!result.has_next) break;
        pg++;
      }
      exportToCSV(allVendors, [
        { label: 'Name', accessor: 'name' },
        { label: 'GSTIN', accessor: 'gstin' },
        { label: 'GSTIN Type', accessor: 'gstin_type' },
        { label: 'PAN', accessor: 'pan' },
        { label: 'Address', accessor: 'address' },
        { label: 'City', accessor: 'city' },
        { label: 'State', accessor: 'state' },
        { label: 'Pincode', accessor: 'pincode' },
        { label: 'Location', accessor: 'location' },
        { label: 'Party Branch', accessor: 'party_branch' },
        { label: 'Contact Person', accessor: 'contact_person' },
        { label: 'Phone', accessor: 'phone' },
        { label: 'Email', accessor: 'email' },
        { label: 'Type', accessor: 'type' },
        { label: 'Currency', accessor: 'currency' },
        { label: 'Category', accessor: 'category' },
        { label: 'Payment Terms', accessor: 'payment_terms' },
        { label: 'Credit Limit', accessor: 'credit_limit' },
        { label: 'Rating', accessor: 'rating' },
        { label: 'Materials', accessor: 'materials' },
      ], 'suppliers_master');
      toast.success(`Exported ${allVendors.length} suppliers`);
    } catch (err) { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  // =============== IMPORT VIEW ===============
  if (showImport) return (
    <div className="space-y-4 p-5 lg:p-6" data-testid="vendor-import">
      <ContactImportWizard
        importType="Supplier"
        onBack={() => setShowImport(false)}
        onComplete={() => { setShowImport(false); loadVendors(); }}
      />
    </div>
  );

  // =============== LIST VIEW ===============
  if (view === 'list') return (
    <div className="space-y-4" data-testid="vendor-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Vendor Management</h2>
          <p className="text-xs text-gray-500">{filtered.length} vendors</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-vendors-btn" onClick={handleExportVendors} disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}Export
          </button>
          {canEdit && (
            <>
              <button data-testid="import-vendors-btn" onClick={() => setShowImport(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Upload className="w-3.5 h-3.5" />Import
              </button>
              <button data-testid="add-vendor-btn" onClick={() => { setForm({ ...emptyForm }); setView('create'); }} className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
                <Plus className="w-3.5 h-3.5" />Add Vendor
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="vendor-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, GSTIN, location..." className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg" />
        </div>
        <select data-testid="vendor-filter-category" value={filterCat} onChange={e => setFilterCat(e.target.value)} className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="vendor-filter-terms" value={filterTerms} onChange={e => setFilterTerms(e.target.value)} className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white">
          <option value="">All Payment Terms</option>
          {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="vendor-sort" value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white">
          <option value="name">Sort: Name</option>
          <option value="outstanding">Sort: Outstanding</option>
          <option value="rating">Sort: Rating</option>
          <option value="credit_limit">Sort: Credit Limit</option>
          <option value="created_at">Sort: Recent</option>
        </select>
      </div>

      {/* Vendor Table */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No vendors found. {canEdit && 'Click "Add Vendor" or "Import" to get started.'}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50">
                {['Vendor Name', 'Contact', 'Category', 'GSTIN', 'Payment Terms', 'Rating', 'Outstanding', 'Actions'].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr></thead>
              <tbody>{filtered.map(v => (
                <React.Fragment key={v.id}>
                  <tr className="border-t border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => setExpandedId(expandedId === v.id ? null : v.id)}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {expandedId === v.id ? <ChevronUp className="w-3 h-3 text-gray-400" /> : <ChevronDown className="w-3 h-3 text-gray-400" />}
                        <div>
                          <p className="font-semibold text-gray-900">{v.name}</p>
                          {v.location && <p className="text-[10px] text-gray-400">{v.location}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {v.contact_person && <p className="text-gray-700">{v.contact_person}</p>}
                      {v.phone && <p className="text-[10px] text-gray-400">{v.phone}</p>}
                    </td>
                    <td className="px-3 py-2.5">{v.category ? <span className="px-1.5 py-0.5 text-[9px] font-medium rounded-full bg-blue-50 text-blue-600">{v.category}</span> : <span className="text-gray-300">-</span>}</td>
                    <td className="px-3 py-2.5 font-mono text-gray-500">{v.gstin || '-'}</td>
                    <td className="px-3 py-2.5 text-gray-600">{v.payment_terms || '-'}</td>
                    <td className="px-3 py-2.5"><Stars value={v.rating || 0} readonly /></td>
                    <td className="px-3 py-2.5">
                      {v.outstanding_balance > 0 ? (
                        <span className="font-bold text-red-600">INR {fmtINR(v.outstanding_balance)}</span>
                      ) : <span className="text-green-600 text-[10px]">Clear</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <button data-testid={`view-vendor-${v.id}`} onClick={() => openDetail(v)} className="p-1 text-gray-400 hover:text-blue-600"><Eye className="w-3.5 h-3.5" /></button>
                        {canEdit && <button data-testid={`edit-vendor-${v.id}`} onClick={() => handleEdit(v)} className="p-1 text-gray-400 hover:text-amber-600"><Edit2 className="w-3.5 h-3.5" /></button>}
                        {user.role === 'Admin' && <button data-testid={`delete-vendor-${v.id}`} onClick={() => handleDelete(v.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    </td>
                  </tr>
                  {expandedId === v.id && (
                    <tr><td colSpan={8} className="px-6 py-3 bg-gray-50/50">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
                        <div><span className="text-gray-400">Email:</span> <span className="text-gray-700">{v.email || '-'}</span></div>
                        <div><span className="text-gray-400">PAN:</span> <span className="text-gray-700">{v.pan || '-'}</span></div>
                        <div><span className="text-gray-400">Type:</span> <span className="text-gray-700 capitalize">{v.type || '-'}</span></div>
                        <div><span className="text-gray-400">Credit Limit:</span> <span className="text-gray-700">INR {fmtINR(v.credit_limit || 0)}</span></div>
                        {v.address && <div className="col-span-2"><span className="text-gray-400">Address:</span> <span className="text-gray-700">{v.address}</span></div>}
                        <div><span className="text-gray-400">City:</span> <span className="text-gray-700">{v.city || '-'}</span></div>
                        <div><span className="text-gray-400">Pincode:</span> <span className="text-gray-700">{v.pincode || '-'}</span></div>
                        <div className="col-span-2"><span className="text-gray-400">Materials:</span> <span className="text-gray-700">{v.materials || '-'}</span></div>
                        <div className="col-span-2"><span className="text-gray-400">Notes:</span> <span className="text-gray-700">{v.notes || '-'}</span></div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // =============== DETAIL VIEW ===============
  if (view === 'detail' && selected) return (
    <div className="space-y-4" data-testid="vendor-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => { setView('list'); setSelected(null); }} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">{selected.name}</h2>
          <p className="text-xs text-gray-500">{selected.type} vendor {selected.category ? `| ${selected.category}` : ''}</p>
        </div>
        {canEdit && <button onClick={() => handleEdit(selected)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}><Edit2 className="w-3 h-3" />Edit</button>}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Info Card */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <h3 className="text-xs font-bold text-gray-900 mb-2">Contact Info</h3>
          {selected.contact_person && <div className="flex items-center gap-2 text-xs"><Building2 className="w-3.5 h-3.5 text-gray-400" />{selected.contact_person}</div>}
          {selected.phone && <div className="flex items-center gap-2 text-xs"><Phone className="w-3.5 h-3.5 text-gray-400" />{selected.phone}</div>}
          {selected.email && <div className="flex items-center gap-2 text-xs"><Mail className="w-3.5 h-3.5 text-gray-400" />{selected.email}</div>}
          {(selected.address || selected.location) && <div className="flex items-start gap-2 text-xs"><MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5" /><div>{selected.address && <div>{selected.address}</div>}{[selected.city, selected.state, selected.pincode].filter(Boolean).join(', ') || selected.location}</div></div>}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex justify-between text-[11px]"><span className="text-gray-400">GSTIN</span><span className="font-mono">{selected.gstin || '-'}</span></div>
            {selected.gstin_type && <div className="flex justify-between text-[11px] mt-1"><span className="text-gray-400">GSTIN Type</span><span>{selected.gstin_type}</span></div>}
            <div className="flex justify-between text-[11px] mt-1"><span className="text-gray-400">PAN</span><span className="font-mono">{selected.pan || '-'}</span></div>
            {selected.party_branch && <div className="flex justify-between text-[11px] mt-1"><span className="text-gray-400">Party Branch</span><span>{selected.party_branch}</span></div>}
            <div className="flex justify-between text-[11px] mt-1"><span className="text-gray-400">Rating</span><Stars value={selected.rating || 0} readonly /></div>
          </div>
        </div>

        {/* Financial Card */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <h3 className="text-xs font-bold text-gray-900 mb-2">Financial</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-[11px]"><span className="text-gray-400">Payment Terms</span><span className="font-medium">{selected.payment_terms || '-'}</span></div>
            <div className="flex justify-between text-[11px]"><span className="text-gray-400">Credit Limit</span><span className="font-medium">INR {fmtINR(selected.credit_limit || 0)}</span></div>
            <div className="flex justify-between text-[11px]"><span className="text-gray-400">Outstanding (Tally)</span>
              <span className={`font-bold ${selected.outstanding_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {selected.outstanding_balance > 0 ? `INR ${fmtINR(selected.outstanding_balance)}` : 'Clear'}
              </span>
            </div>
            <div className="flex justify-between text-[11px]"><span className="text-gray-400">Currency</span><span>{selected.currency}</span></div>
          </div>
          {selected.bank_details && (selected.bank_details.account_number || selected.bank_details.bank_name) && (
            <div className="pt-2 border-t border-gray-100">
              <p className="text-[10px] font-semibold text-gray-500 mb-1">Bank Details</p>
              <div className="space-y-1 text-[11px]">
                {selected.bank_details.bank_name && <div><span className="text-gray-400">Bank:</span> {selected.bank_details.bank_name}</div>}
                {selected.bank_details.branch_name && <div><span className="text-gray-400">Branch:</span> {selected.bank_details.branch_name}</div>}
                {selected.bank_details.account_number && <div><span className="text-gray-400">A/C:</span> <span className="font-mono">{selected.bank_details.account_number}</span></div>}
                {selected.bank_details.ifsc_code && <div><span className="text-gray-400">IFSC:</span> <span className="font-mono">{selected.bank_details.ifsc_code}</span></div>}
              </div>
            </div>
          )}
        </div>

        {/* Documents Card */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-900">Documents ({(selected.documents || []).length})</h3>
          </div>
          {canEdit && (
            <div className="flex gap-2 mb-3">
              <select value={docType} onChange={e => setDocType(e.target.value)} className="flex-1 px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg bg-white">
                {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              <label className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg cursor-pointer" style={{ backgroundColor: '#24549c' }}>
                {uploadingDoc ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}Upload
                <input type="file" className="hidden" onChange={e => e.target.files[0] && handleDocUpload(selected.id, e.target.files[0])} disabled={uploadingDoc} />
              </label>
            </div>
          )}
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {(selected.documents || []).length === 0 ? (
              <p className="text-[11px] text-gray-400 text-center py-4">No documents uploaded</p>
            ) : (selected.documents || []).map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-blue-500" />
                  <div>
                    <p className="text-[11px] font-medium text-gray-800 truncate max-w-[140px]">{doc.name}</p>
                    <p className="text-[9px] text-gray-400">{DOC_TYPES.find(d => d.value === doc.doc_type)?.label || doc.doc_type}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  {doc.file_url && <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="p-1 text-gray-400 hover:text-blue-600"><Download className="w-3 h-3" /></a>}
                  {canEdit && <button onClick={() => handleDocDelete(selected.id, doc.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tally Payable Details */}
      {(selected.payable_details || []).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100"><h3 className="text-xs font-bold text-gray-900">Tally Payable Details</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50">{['Bill #', 'Date', 'Due', 'Amount', 'Paid', 'Balance', 'Overdue', 'Bucket'].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">{h}</th>)}</tr></thead>
              <tbody>{selected.payable_details.map((p, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="px-3 py-2 font-mono">{p.bill_number}</td>
                  <td className="px-3 py-2 text-gray-500">{p.bill_date}</td>
                  <td className="px-3 py-2 text-gray-500">{p.due_date}</td>
                  <td className="px-3 py-2 font-semibold">INR {fmtINR(p.amount)}</td>
                  <td className="px-3 py-2 text-green-600">{fmtINR(p.paid_amount)}</td>
                  <td className="px-3 py-2 font-bold text-red-600">{fmtINR(p.balance)}</td>
                  <td className="px-3 py-2">{p.days_overdue > 0 ? <span className="text-red-600 font-bold">{p.days_overdue}d</span> : '-'}</td>
                  <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full ${p.aging_bucket === 'Current' ? 'bg-green-100 text-green-700' : p.aging_bucket === '90+' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{p.aging_bucket}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {selected.notes && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-gray-900 mb-2">Notes</h3>
          <p className="text-xs text-gray-600 whitespace-pre-wrap">{selected.notes}</p>
        </div>
      )}
    </div>
  );

  // =============== CREATE / EDIT FORM ===============
  return (
    <div className="space-y-4" data-testid="vendor-form">
      <div className="flex items-center gap-3">
        <button onClick={() => { setView('list'); setForm({ ...emptyForm }); setSelected(null); }} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
        <h2 className="text-lg font-bold text-gray-900">{view === 'edit' ? 'Edit Vendor' : 'New Vendor'}</h2>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
        {/* Basic Info */}
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Basic Information</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Vendor Name *</label>
            <input data-testid="vendor-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" placeholder="Enter vendor name" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Category</label>
            <select data-testid="vendor-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white">
              <option value="">Select category</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Type</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white">
              <option value="domestic">Domestic</option>
              <option value="import">Import</option>
            </select></div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Contact Person</label>
            <input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Phone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
        </div>
        {/* Address Details */}
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider pt-2">Address</h3>
        <div className="grid md:grid-cols-1 gap-3">
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Address</label>
            <input data-testid="vendor-address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" placeholder="Street address, building, area" /></div>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">City</label>
            <input data-testid="vendor-city" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">State</label>
            <input data-testid="vendor-state" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Pincode</label>
            <input data-testid="vendor-pincode" value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" maxLength={6} /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Location (Short)</label>
            <input data-testid="vendor-location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" placeholder="e.g. Ranchi, Jharkhand" /></div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Materials / Services</label>
            <input value={form.materials} onChange={e => setForm({ ...form, materials: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" placeholder="e.g. Paint, Glass Beads" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Party Branch</label>
            <input data-testid="vendor-party-branch" value={form.party_branch} onChange={e => setForm({ ...form, party_branch: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">GSTIN Type</label>
            <select data-testid="vendor-gstin-type" value={form.gstin_type} onChange={e => setForm({ ...form, gstin_type: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white">
              <option value="">Select...</option>
              <option value="Regular">Regular</option>
              <option value="Composition">Composition</option>
              <option value="Unregistered">Unregistered</option>
              <option value="Consumer">Consumer</option>
              <option value="SEZ">SEZ</option>
            </select></div>
        </div>

        {/* Tax & Compliance */}
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider pt-2">Tax & Compliance</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">GSTIN</label>
            <input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg font-mono" maxLength={15} /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">PAN</label>
            <input value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg font-mono" maxLength={10} /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Currency</label>
            <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white">
              <option value="INR">INR</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="CNY">CNY</option>
            </select></div>
        </div>

        {/* Financial */}
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider pt-2">Financial</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Payment Terms</label>
            <select value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white">
              <option value="">Select...</option>
              {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Credit Limit (INR)</label>
            <input type="number" value={form.credit_limit} onChange={e => setForm({ ...form, credit_limit: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Rating</label>
            <Stars value={form.rating} onChange={v => setForm({ ...form, rating: v })} /></div>
        </div>

        {/* Bank Details */}
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider pt-2">Bank Details</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Bank Name</label>
            <input value={form.bank_details?.bank_name || ''} onChange={e => setForm({ ...form, bank_details: { ...form.bank_details, bank_name: e.target.value } })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Branch</label>
            <input value={form.bank_details?.branch_name || ''} onChange={e => setForm({ ...form, bank_details: { ...form.bank_details, branch_name: e.target.value } })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Account Number</label>
            <input value={form.bank_details?.account_number || ''} onChange={e => setForm({ ...form, bank_details: { ...form.bank_details, account_number: e.target.value } })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg font-mono" /></div>
          <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">IFSC Code</label>
            <input value={form.bank_details?.ifsc_code || ''} onChange={e => setForm({ ...form, bank_details: { ...form.bank_details, ifsc_code: e.target.value.toUpperCase() } })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg font-mono" maxLength={11} /></div>
        </div>

        {/* Notes */}
        <div><label className="text-[11px] font-medium text-gray-600 mb-1 block">Notes / Remarks</label>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg resize-none" placeholder="Any additional notes..." /></div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button onClick={() => { setView('list'); setForm({ ...emptyForm }); setSelected(null); }} className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button data-testid="save-vendor-btn" onClick={handleSave} disabled={submitting} className="flex items-center gap-1.5 px-6 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{view === 'edit' ? 'Update Vendor' : 'Create Vendor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorManagement;
